import {
  type __InputStylesNames,
  Button,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  Textarea,
  type TextInputProps,
  Tooltip,
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import type { ReactElement } from 'react'
import { cloneElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { AISplitButton } from '@/components/AISplitButton'
import { InputLabel } from '@/components/InputLabel'
import { theme } from '@/configs/theme'
import type { AIFieldState } from '@/hooks/types/aiFieldGeneration'
import * as classes from '@/styles/AIInputWrapper.css'

type AIInputWrapperProps = {
  fieldState: AIFieldState
  children: ReactElement<TextInputProps>
  onAskSidekick: () => void
  onAccept: () => void
  onReject: () => void
  onRegenerate?: () => void
  onCancel?: () => void
  /** When provided, "Guide…" appears in idle dropdown; user can add instructions sent as user_instructions to the generate endpoint. */
  onAskSidekickWithGuide?: (instructions: string) => void
  disabled?: boolean
  /**
   * Whether the saved value is still the machine's, from `field_sources` — see
   * `utils/fieldProvenance`. Drives the AI badge; leave it out for a field with no
   * provenance to read.
   */
  isGenerated?: boolean
}

/**
 * Badge state between saves. The prop is the server's answer and only moves when the
 * form is saved and refetched, so typing has to hide the badge locally or it keeps
 * crediting the machine for text the expert just replaced. Accepting a suggestion sets
 * it the other way, and the override is dropped whenever the server's answer changes.
 */
export function useGeneratedBadge(
  isGenerated: boolean | undefined,
  fieldState: AIFieldState,
  onAccept: () => void,
  onReject: () => void,
) {
  const [override, setOverride] = useState<boolean | null>(null)

  useEffect(() => {
    setOverride(null)
  }, [isGenerated])

  // A generation that lands makes the value the machine's again however it was accepted —
  // the auto-accept path never calls `handleAccept`. Keyed on it landing rather than on it
  // starting, so a cancelled or failed one leaves the badge exactly as it was: the field
  // still holds whatever it held before, and claiming that for the Sidekick is the wrong
  // way to be wrong.
  //
  // The whole transition is tracked, not just the outcome. The outcome is set while the
  // field is still `pending` and stays set afterwards, so comparing outcomes alone both
  // misses the second generation of a field and re-badges an edited one on remount.
  const previousRef = useRef({ state: fieldState.state, outcome: fieldState.lastOutcome })
  useEffect(() => {
    const previous = previousRef.current
    previousRef.current = { state: fieldState.state, outcome: fieldState.lastOutcome }

    if (fieldState.state !== 'idle' || fieldState.lastOutcome !== 'succeeded') return
    // Came to rest here, rather than already having been at rest when this mounted.
    if (previous.state !== 'idle' || previous.outcome !== 'succeeded') setOverride(true)
  }, [fieldState.state, fieldState.lastOutcome])

  return {
    showBadge: override ?? !!isGenerated,
    markEdited: useCallback(() => setOverride(false), []),
    handleAccept: useCallback(() => {
      setOverride(true)
      onAccept()
    }, [onAccept]),
    handleReject: useCallback(() => {
      setOverride(null)
      onReject()
    }, [onReject]),
  }
}

function useStatusDisplay(fieldState: AIFieldState) {
  return {
    statusText: fieldState.state === 'loading' ? 'Processing' : '',
  }
}

export const GENERATION_FAILED_MESSAGE = "Sidekick couldn't write this one. Use Ask Sidekick to try again."

/**
 * A failed generation has nowhere else to appear.
 *
 * It used to be reported by the helper line under "Done Adding Files", which SME-1135
 * removed — so without this a pass that fails leaves a required field silently blank and
 * the expert meets it as an error at save, which is the experience that ticket exists to
 * end.
 *
 * Self-clearing: `lastOutcome` is cleared when the next request starts and set to
 * `succeeded` when one lands, so a retry takes the message with it. Typing dismisses it
 * too, since the expert writing the field themselves is the other way the problem goes.
 */
export function useGenerationError(fieldState: AIFieldState, isEmpty: boolean) {
  // Only when the field is actually empty. A pass the expert never asked for regenerates
  // fields that already hold good text (decision 46 refreshes anything still the machine's),
  // and a failure there leaves nothing wrong on screen — marking it red, and forcing Advanced
  // open around it, reports a problem the expert cannot see and does not have.
  const failed = fieldState.state === 'idle' && fieldState.lastOutcome === 'failed' && isEmpty
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    if (!failed) setDismissed(false)
  }, [failed])

  return {
    message: failed && !dismissed ? GENERATION_FAILED_MESSAGE : null,
    dismiss: useCallback(() => setDismissed(true), []),
  }
}

function useInputModification(
  originalProps: TextInputProps,
  fieldState: AIFieldState,
  onReject: () => void,
  onEdit: () => void,
  onCancel?: () => void,
) {
  const { state, suggestion } = fieldState
  const isPending = state === 'pending'
  const isLoading = state === 'loading'
  const isAIActive = isPending || isLoading

  // On pending → idle transition, briefly force controlled mode so the DOM
  // shows the restored value (defaultValue alone won't update a mounted input).
  const prevStateRef = useRef(state)
  const [forceControlledValue, setForceControlledValue] = useState<string | null>(null)

  if (prevStateRef.current === 'pending' && state === 'idle') {
    const restoredValue = originalProps.defaultValue ?? originalProps.value ?? ''
    if (forceControlledValue === null) {
      setForceControlledValue(String(restoredValue))
    }
  }
  prevStateRef.current = state

  useEffect(() => {
    if (forceControlledValue !== null) setForceControlledValue(null)
  }, [forceControlledValue])

  return useMemo(() => {
    const classNames = originalProps.classNames as Partial<Record<__InputStylesNames, string>>

    const valueProps: Pick<TextInputProps, 'value' | 'defaultValue'> = isPending
      ? { value: suggestion ?? '', defaultValue: undefined }
      : forceControlledValue !== null
        ? { value: forceControlledValue, defaultValue: undefined }
        : originalProps.value !== undefined
          ? { value: originalProps.value }
          : {}

    return {
      ...originalProps,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        if (isPending) onReject()
        if (isLoading) onCancel?.()
        onEdit()
        // Clear force-controlled so the keystroke takes effect in uncontrolled mode
        if (forceControlledValue !== null) {
          setForceControlledValue(null)
        }
        originalProps.onChange?.(e)
      },
      ...valueProps,
      withAsterisk: false,
      variant: isAIActive ? 'filled' : originalProps.variant,
      classNames: {
        ...originalProps.classNames,
        input: theme.cx(classNames?.input, isAIActive && classes.input),
      },
      error: originalProps.error,
    }
  }, [
    originalProps,
    fieldState,
    onReject,
    onEdit,
    onCancel,
    forceControlledValue,
    isPending,
    isLoading,
    suggestion,
    isAIActive,
  ])
}

// Guide modal content: instructions textarea + Generate / Cancel (exported for reuse in FAQEntry)
export function GuideModalContent({
  instructions,
  onInstructionsChange,
  onGenerate,
  onClose,
  disabled,
}: {
  instructions: string
  onInstructionsChange: (value: string) => void
  onGenerate: () => void
  onClose: () => void
  disabled?: boolean
}) {
  const handleGenerate = useCallback(() => {
    onGenerate()
    onClose()
  }, [onGenerate, onClose])

  return (
    <Stack gap="md">
      <Textarea
        label="Instructions for Sidekick"
        placeholder="e.g. Use a professional tone"
        value={instructions}
        onChange={e => onInstructionsChange(e.target.value)}
        autosize
        minRows={3}
        maxRows={6}
        disabled={disabled}
      />
      <Group gap="sm" justify="flex-end">
        <Button variant="default" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleGenerate} disabled={disabled}>
          Generate
        </Button>
      </Group>
    </Stack>
  )
}

// Action buttons component
function ActionButtons({
  fieldState,
  onAskSidekick,
  onAccept,
  onReject,
  onRegenerate,
  onCancel,
  onAskSidekickWithGuide,
  disabled,
  statusText,
}: {
  fieldState: AIFieldState
  onAskSidekick: () => void
  onAccept: () => void
  onReject: () => void
  onRegenerate?: () => void
  onCancel?: () => void
  onAskSidekickWithGuide?: (instructions: string) => void
  disabled?: boolean
  statusText: string
}) {
  const [guideOpen, setGuideOpen] = useState(false)
  const [guideInstructions, setGuideInstructions] = useState('')
  const isNarrowViewport = !useMediaQuery(`(min-width: ${theme.breakpoints.sm})`)

  const handleGuideGenerate = useCallback(() => {
    if (onAskSidekickWithGuide) {
      onAskSidekickWithGuide(guideInstructions.trim())
      setGuideInstructions('')
    }
  }, [onAskSidekickWithGuide, guideInstructions])

  const handleGuideClose = useCallback(() => {
    setGuideOpen(false)
    setGuideInstructions('')
  }, [])

  const buttons = (
    <AISplitButton
      fieldState={fieldState.state}
      onAskSidekick={onAskSidekick}
      onAccept={onAccept}
      onReject={onReject}
      onRegenerate={onRegenerate}
      onCancel={onCancel}
      onGuideClick={onAskSidekickWithGuide ? () => setGuideOpen(true) : undefined}
      disabled={disabled}
    />
  )

  const isLoading = fieldState.state === 'loading'

  const statusDisplay = statusText ? (
    <Group gap={4} align="center" wrap="nowrap">
      <Text fw={600} tt="uppercase" size="xs" variant="gradient">
        {statusText}
      </Text>
      {isLoading && <Loader size={12} />}
    </Group>
  ) : null

  if (!onAskSidekickWithGuide) {
    return (
      <Group align="center" gap="xs">
        {statusDisplay}
        {buttons}
      </Group>
    )
  }

  return (
    <Group align="center" gap="xs">
      {statusDisplay}
      {buttons}
      <Modal
        title="Guide Sidekick"
        opened={guideOpen}
        onClose={handleGuideClose}
        centered
        size="sm"
        fullScreen={isNarrowViewport}>
        <GuideModalContent
          instructions={guideInstructions}
          onInstructionsChange={setGuideInstructions}
          onGenerate={handleGuideGenerate}
          onClose={handleGuideClose}
          disabled={disabled}
        />
      </Modal>
    </Group>
  )
}

export function AIInputWrapper(props: AIInputWrapperProps) {
  const {
    fieldState,
    onAskSidekick,
    onAccept,
    onReject,
    onRegenerate,
    onCancel,
    onAskSidekickWithGuide,
    disabled,
    isGenerated,
    children,
  } = props

  const statusDisplay = useStatusDisplay(fieldState)
  const badge = useGeneratedBadge(isGenerated, fieldState, onAccept, onReject)
  const currentValue = children.props.defaultValue ?? children.props.value ?? ''
  const generationError = useGenerationError(fieldState, !String(currentValue).trim())
  const { markEdited } = badge
  const { dismiss: dismissGenerationError } = generationError
  const handleEdit = useCallback(() => {
    markEdited()
    dismissGenerationError()
  }, [markEdited, dismissGenerationError])
  const modifiedInputProps = useInputModification(children.props, fieldState, badge.handleReject, handleEdit, onCancel)

  if (disabled) return children

  const enhancedProps: TextInputProps = {
    ...modifiedInputProps,
    // The generation message wins where it applies at all, which is only an empty field. The
    // validation error it displaces there says "Title is required." — true, but the expert can
    // see that; what they cannot see is that the Sidekick tried and failed, and what to do
    // next. Anywhere else this is null and the field's own error is untouched.
    error: generationError.message ?? modifiedInputProps.error,
    label: (
      <Group justify="space-between" align="center">
        <Group gap={theme.rem(6)} align="center" wrap="nowrap">
          <InputLabel label={children.props.label} withAsterisk={children.props.withAsterisk} withSpacer={false} />

          {badge.showBadge && fieldState.state === 'idle' && (
            <Tooltip label="Generated by Sidekick. Edit this field and your edits will never be overwritten.">
              <Text component="span" className={classes.aiBadge}>
                AI
              </Text>
            </Tooltip>
          )}
        </Group>

        <ActionButtons
          fieldState={fieldState}
          onAskSidekick={onAskSidekick}
          onAccept={badge.handleAccept}
          onReject={badge.handleReject}
          onRegenerate={onRegenerate}
          onCancel={onCancel}
          onAskSidekickWithGuide={onAskSidekickWithGuide}
          disabled={disabled}
          statusText={statusDisplay.statusText}
        />
      </Group>
    ),
  }

  return cloneElement(children, enhancedProps)
}
